<script setup>
import { reactive, ref, onMounted, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import Logo from '@/Components/Logo.vue';

const authStore = useAuthStore();

const form = reactive({
	email: '',
	password: '',
	isProcessing: false
});

const errorMessage = ref('');

const isLoggedIn = computed(() => {
	return authStore.isLoggedIn;
});

const labels = ref(window.portal.labels);

const login = async () => {
	if (form.isProcessing) return;
	form.isProcessing = true;

    errorMessage.value = ''; // Clear previous errors

    try {
        // Step 1: Get CSRF Cookie
        await axios.get(window.portal.cookie_url);

    	// Step 2: Attempt Login
    	const response = await axios.post(window.portal.login_url, {
    		email: form.email,
    		password: form.password
    	});

    	// Step 3: Store User Data
    	authStore.login(response.data);

    	// Close the modal
    	let close = document.querySelector('#sign-in .close');

    	if (close) {
    		close.submit();
    	}

    	//If we're doing event registration, refresh the iframe
    	let iframe = document.querySelector('body.event-registration #event-registration-frame');

    	if (iframe) {
    		iframe.src = iframe.dataset.src;
    	}
    } catch (error) {
        // Step 4: Handle Errors
        if (error.response) {
            errorMessage.value = error.response.data.message || 'Invalid credentials';
        } else {
            errorMessage.value = 'An error occurred. Please try again.';
        }
    } finally {
    	form.isProcessing = false;
    }
};

onMounted(async () => {
	//See if we logged in from WordPress this session
	if (isLoggedIn.value) {
		try {
			const response = await axios.get(window.portal.auth_check_url);
			
			authStore.login(response.data);
		} catch (error) {
			//If we are no longer logged in, then clear the stuff
			authStore.logout();
		}
	}
	//Otherwise, see if we logged in from Laravel this session
	else {
		const cookies = document.cookie.split('; ');

		// Check for the specific cookie
		for (let cookie of cookies) {
			const [name, value] = cookie.split('=');

			if (name === 'portal_auth') {
				//Now we can try again, but without the API token
				try {
					const response = await axios.get(window.portal.auth_check_url);
					
					authStore.login(response.data);
				} catch (error) {
					//If we are no longer logged in, then clear the stuff
					authStore.logout();
				}

				break;
			}
		}
	}
});
</script>

<template>
	<div class="wp-block-group is-vertical is-content-justification-center is-layout-flex">
		<Logo/>

		<h2 class="has-h-4-font-size" v-html="labels.sign_in" style="text-align: center;"></h2>

		<div class="wp-block-group is-layout-flow" v-if="errorMessage" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--red-700); background-color: var(--wp--preset--color--red-100); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
			<p class="has-small-font-size has-text-color has-red-700-color" v-html="errorMessage"></p>
		</div>

		<div class="wp-block-group is-layout-flow" v-if="isLoggedIn" style="border-radius: var(--wp--custom--border--radius-10); border: solid 1px var(--wp--preset--color--fc-green-800); background-color: var(--wp--preset--color--fc-green-50); padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);">
			<p class="has-small-font-size has-text-color has-fc-green-800-color" v-html="labels.sign_in_success"></p>
		</div>

		<form @submit.prevent="login" class="wp-block-group is-vertical is-content-justification-center is-layout-flex login-form" v-else>
			<div>
				<label class="screen-reader-text" for="portal_email" v-html="labels.email"></label>

				<input id="portal_email" type="email" required v-model="form.email" autocomplete="email" :placeholder="labels.email">
			</div>

			<div>
				<label class="screen-reader-text" for="portal_password" v-html="labels.password"></label>

				<input id="portal_password" type="password" required v-model="form.password" autocomplete="current-password" :placeholder="labels.password">
			</div>

			<button type="submit" :disabled="form.isProcessing" class="submit-button">
				<span v-if="form.isProcessing" class="spinner"></span>
				<span v-else v-html="labels.sign_in"></span>
			</button>
		</form>
	</div>
</template>
